import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { LoaderService } from 'src/app/services/loader.service';
import { debounce, finalize, takeUntil } from 'rxjs/operators';
import { ServiceordreService } from 'src/app/api/services';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { interval, Subject } from 'rxjs';
import { CreateServiceOrdreComponentDialogData, CreateTilkaldInformation } from 'src/app/components/aftale/models';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { HttpErrorResponse } from '@angular/common/http';
import { OpgaveEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/opgave-enum';
import { ServiceordreDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceordre-dto';
import { CreateServiceordreRequestDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/create-serviceordre-request-dto';
import { ResendServiceordredRequestDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/resend-serviceordred-request-dto';
import { ServiceaftaleDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceaftale-dto';
import { NotificationService } from 'src/app/notifications/services/notification.service';
import { ConfirmCreateDialogService } from '../../confirm-create-dialog/confirm-create-dialog.service';
import { KreditvurderingDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/kreditvurdering-dto';
import { KreditvaerdigEnumDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/kreditvaerdig-enum-dto';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';

@Component({
  selector: 'app-create',
  templateUrl: './create-tilkald.component.html',
  styleUrls: ['./create-tilkald.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CreateTilkaldComponent implements OnInit, OnDestroy {
  kreditvaerdigEnum = KreditvaerdigEnumDto;

  form: UntypedFormGroup;
  createTilkaldInformation: CreateTilkaldInformation;
  serviceaftale: ServiceaftaleDto;
  serviceordre: ServiceordreDto;
  today: Date = new Date();
  validationErrors: any[] = [];
  kreditvurdering: KreditvurderingDto;
  opgavetype: OpgaveEnum;
  partnerEndateSoon: boolean = false;

  isLoading = false;

  unsubscribe$ = new Subject<void>();

  get note() {
    return this.form.get('note');
  }

  get fastOrdreNote() {
    return this.form.get('fastOrdreNote');
  }

  get accept() {
    return this.form.get('accept');
  }
  get acceptTekst() {
    return this.form.get('acceptTekst');
  }
  get fakturaNote() {
    return this.form.get('fakturaNote');
  }

  get erTilkald() {
    return this.opgavetype == OpgaveEnum.Tilkald;
  }

  constructor(
    private serviceordreService: ServiceordreService,
    private loader: LoaderService,
    private formBuilder: UntypedFormBuilder,
    private errorHandler: ErrorHandlerService,
    private notification: NotificationService,
    private dialogRef: MatDialogRef<CreateTilkaldComponent>,
    private confirmCreateDialogService: ConfirmCreateDialogService,
    @Inject(MAT_DIALOG_DATA) public data: CreateServiceOrdreComponentDialogData,
  ) {
    this.serviceaftale = data.serviceaftale;
    //This is apparently the simplest way to test if a date is within 30 days in pure javascript
    var date60DaysInFuture = new Date(new Date().getTime() + 30 * 24 * 60 * 60 * 1000);
    this.partnerEndateSoon = data.serviceaftale.partner.validTo && new Date(data.serviceaftale.partner.validTo) < date60DaysInFuture;
    this.serviceordre = data.serviceordre;
    this.kreditvurdering = data.kreditvurdering;
    this.opgavetype = data.opgavetype;
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      deadline: [new Date(), Validators.required],
      note: [this.data.serviceordre?.description, [Validators.required, Validators.maxLength(500)]],
      fastOrdreNote: [this.data.serviceaftale.fastOrdreNote, [Validators.maxLength(500)]],
      accept: [this.kreditvurdering?.kreditvaerdig == KreditvaerdigEnumDto.Yes],
      acceptTekst: [null, [Validators.maxLength(32)]],
      fakturaNote: [this.data.serviceordre?.fakturaNote, [Validators.maxLength(500)]],
      genudkald: [false],
      telefonservice: [false],
    });

    this.loader.loadingStateChanged$.subscribe((isLoading) => {
      this.isLoading = isLoading;
    });

    if (
      this.kreditvurdering?.kreditvaerdig == KreditvaerdigEnumDto.No ||
      this.kreditvurdering?.kreditvaerdig == KreditvaerdigEnumDto.Maybe ||
      this.kreditvurdering?.kreditvaerdig == KreditvaerdigEnumDto.NotSet
    ) {
      this.accept.setValidators([Validators.requiredTrue]);
      this.acceptTekst.setValidators([Validators.required, Validators.maxLength(32)]);
    }

    this.form.valueChanges
      .pipe(
        takeUntil(this.unsubscribe$),
        debounce(() => interval(750)),
      )
      .subscribe((createTilvalgInformation) => {
        this.createTilkaldInformation = createTilvalgInformation;
      });

    // Hvis data er medsendt i dialog, skal form opdateres for at initalisere createTilvalgInformation
    if (this.data) this.form.updateValueAndValidity();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  async onSubmit() {
    if (
      await this.confirmCreateDialogService.Confirm(
        this.serviceaftale.serviceAftaleNo,
        this.opgavetype,
        this.createTilkaldInformation.deadline,
      )
    ) {
      if (this.serviceordre) {
        this.resendTilkald();
      } else {
        this.createTilkald();
      }
    }
  }

  createTilkald() {
    this.createTilkaldInformation = this.form.value;

    const request: CreateServiceordreRequestDto = {
      deadline: this.createTilkaldInformation.deadline.toOkDateString(),
      serviceaftaleNo: this.serviceaftale.serviceAftaleNo,
      serviceordreType: this.opgavetype,
      description: this.createTilkaldInformation.note,
      fastOrdreNote: this.createTilkaldInformation.fastOrdreNote,
      kreditvurderingAccept: this.createTilkaldInformation.accept
        ? {
            aarsagTekst: this.kreditvurdering.aarsagTekst,
            acceptTekst: this.acceptTekst.value,
            internalG4KreditkodeId: this.kreditvurdering.internalG4KreditkodeId,
            okKundeAftalenr: this.serviceaftale.okKundeAftaleNr,
            kreditvaerdig: this.kreditvurdering.kreditvaerdig,
          }
        : null,
      fakturaNote: this.createTilkaldInformation.fakturaNote,
      tags: [],
    };

    if (this.createTilkaldInformation.genudkald) {
      request.tags.push({ serviceordreTagNo: 1 });
    }

    if (this.createTilkaldInformation.telefonservice) {
      request.tags.push({ serviceordreTagNo: 2 });
    }

    this.loader.show();
    this.serviceordreService
      .createServiceordre({ body: request, caller: EGIS_SERVICE_CALLER })
      .pipe(
        finalize(() => {
          this.loader.hide();
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(
        () => {
          this.notification.success(this.opgavetype + ' oprettet.');
          this.dialogRef.close();
        },
        (error: HttpErrorResponse) => {
          if (error.status === 400) {
            this.validationErrors = [];

            for (var errorArray in error.error) {
              if (error.error.hasOwnProperty(errorArray)) {
                this.validationErrors = this.validationErrors.concat(error.error[errorArray]);
              }
            }
          } else {
            this.errorHandler.HandleError(error);
          }
        },
      );
  }

  resendTilkald() {
    this.createTilkaldInformation = this.form.value;

    const request: ResendServiceordredRequestDto = {
      serviceordreNo: this.serviceordre.serviceordreNo,
      deadline: this.createTilkaldInformation.deadline.toOkDateString(),
      description: this.createTilkaldInformation.note,
      fastOrdreNote: this.createTilkaldInformation.fastOrdreNote,
      fakturaNote: this.createTilkaldInformation.fakturaNote,
      kreditadvarselAccept: this.createTilkaldInformation.accept
        ? {
            aarsagTekst: this.kreditvurdering.aarsagTekst,
            acceptTekst: this.acceptTekst.value,
            internalG4KreditkodeId: this.kreditvurdering.internalG4KreditkodeId,
            okKundeAftalenr: this.serviceaftale.okKundeAftaleNr,
            kreditvaerdig: this.kreditvurdering.kreditvaerdig,
          }
        : null,
    };

    this.loader.show();
    this.serviceordreService
      .resendServiceordre({ body: request, caller: EGIS_SERVICE_CALLER })
      .pipe(
        finalize(() => {
          this.loader.hide();
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(
        () => {
          this.notification.success('Tilkald genfremsendt.');
          this.dialogRef.close();
        },
        (error: HttpErrorResponse) => {
          this.errorHandler.HandleError(error);
        },
      );
  }
}
